import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const precalculusUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Trigonometric and Polar Functions",
  examWeight: "30–35%",
  focus: "Circular Motion and Geometry for Periodic Phenomena",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: Periodic Phenomena",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition of Periodic Functions</h3>
            <KeyTerm 
              term="Periodic Function" 
              definition="A function f is periodic if there exists a positive number p such that f(x + p) = f(x) for all x in the domain. The smallest such p is the fundamental period."
            />
            <p className="text-slate-700 leading-relaxed mt-4">
              As input values increase, output values demonstrate a repeating pattern over successive equal-length intervals.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Constructing Graphs</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-slate-700">
                <strong>One Cycle:</strong> The graph can be fully constructed by knowing the behavior over a single cycle (an interval of length <InlineMath math="p" />).
              </p>
              <p className="text-slate-700 mt-2">
                <strong>Repetition:</strong> Copy and paste the single cycle shape horizontally to extend the graph.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Characteristics</h3>
            <KeyTerm 
              term="Period" 
              definition="The length of one complete cycle. Can be found by measuring the distance between consecutive maximums or minimums."
            />
            <ImportantNote>
              If a function is periodic, any characteristic (like a local max or an interval of increase) found in one period will recur in every subsequent period.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Sine, Cosine, and Tangent",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Standard Position and Radians</h3>
            <KeyTerm 
              term="Standard Position" 
              definition="An angle θ in the coordinate plane with its vertex at the origin (0,0) and its initial ray along the positive x-axis."
            />
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-sm text-slate-700"><strong>Positive θ:</strong> Counter-clockwise rotation</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-sm text-slate-700"><strong>Negative θ:</strong> Clockwise rotation</p>
              </div>
            </div>
            <KeyTerm 
              term="Radian Measure" 
              definition="The ratio of the arc length s to the radius r (θ = s/r). On a unit circle (r = 1), the radian measure equals the arc length."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Trig Functions on the Unit Circle</h3>
            <p className="text-slate-700 mb-4">
              Let <InlineMath math="P(x, y)" /> be the point where the terminal ray of angle <InlineMath math="\theta" /> intersects the unit circle (<InlineMath math="x^2 + y^2 = 1" />).
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Sine (<InlineMath math="\sin \theta" />)</h4>
                <p className="text-sm text-slate-700 mt-1">The <strong>y-coordinate</strong> of point P. Represents vertical displacement from the x-axis.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Cosine (<InlineMath math="\cos \theta" />)</h4>
                <p className="text-sm text-slate-700 mt-1">The <strong>x-coordinate</strong> of point P. Represents horizontal displacement from the y-axis.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Tangent (<InlineMath math="\tan \theta" />)</h4>
                <p className="text-sm text-slate-700 mt-1">The <strong>slope</strong> of the terminal ray: <InlineMath math="\tan \theta = \frac{y}{x} = \frac{\sin \theta}{\cos \theta}" /></p>
                <p className="text-sm text-slate-600 mt-1">Undefined when <InlineMath math="x = 0" /> (at <InlineMath math="\theta = \frac{\pi}{2} + n\pi" />)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Sine and Cosine Function Values",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Coordinates on a Circle of Radius r</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <p className="text-slate-700">If the circle has radius <InlineMath math="r" /> (not just 1):</p>
              <BlockMath math="P = (r \cos \theta, \, r \sin \theta)" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Special Angles (Must Memorize!)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-slate-200 text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-2 border">θ (Rad)</th>
                    <th className="px-4 py-2 border">θ (Deg)</th>
                    <th className="px-4 py-2 border">cos θ (x)</th>
                    <th className="px-4 py-2 border">sin θ (y)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="px-4 py-2 border text-center">0</td><td className="px-4 py-2 border text-center">0°</td><td className="px-4 py-2 border text-center">1</td><td className="px-4 py-2 border text-center">0</td></tr>
                  <tr><td className="px-4 py-2 border text-center"><InlineMath math="\pi/6" /></td><td className="px-4 py-2 border text-center">30°</td><td className="px-4 py-2 border text-center"><InlineMath math="\sqrt{3}/2" /></td><td className="px-4 py-2 border text-center"><InlineMath math="1/2" /></td></tr>
                  <tr><td className="px-4 py-2 border text-center"><InlineMath math="\pi/4" /></td><td className="px-4 py-2 border text-center">45°</td><td className="px-4 py-2 border text-center"><InlineMath math="\sqrt{2}/2" /></td><td className="px-4 py-2 border text-center"><InlineMath math="\sqrt{2}/2" /></td></tr>
                  <tr><td className="px-4 py-2 border text-center"><InlineMath math="\pi/3" /></td><td className="px-4 py-2 border text-center">60°</td><td className="px-4 py-2 border text-center"><InlineMath math="1/2" /></td><td className="px-4 py-2 border text-center"><InlineMath math="\sqrt{3}/2" /></td></tr>
                  <tr><td className="px-4 py-2 border text-center"><InlineMath math="\pi/2" /></td><td className="px-4 py-2 border text-center">90°</td><td className="px-4 py-2 border text-center">0</td><td className="px-4 py-2 border text-center">1</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Signs by Quadrant (ASTC)</h3>
            <Mnemonic 
              title="All Students Take Calculus"
              content="Q1: All positive | Q2: Sine positive | Q3: Tangent positive | Q4: Cosine positive"
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Sine and Cosine Function Graphs",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Sine Graph</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="font-semibold text-blue-800"><InlineMath math="f(\theta) = \sin \theta" /></p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><strong>Interpretation:</strong> Tracking the height (y-value) of a point rotating around the unit circle</li>
                <li><strong>Shape:</strong> Starts at <InlineMath math="(0,0)" />, up to <InlineMath math="(\frac{\pi}{2}, 1)" />, down through <InlineMath math="(\pi, 0)" /> to <InlineMath math="(\frac{3\pi}{2}, -1)" />, back to <InlineMath math="(2\pi, 0)" /></li>
                <li><strong>Symmetry:</strong> Odd function: <InlineMath math="\sin(-\theta) = -\sin \theta" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Cosine Graph</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <p className="font-semibold text-green-800"><InlineMath math="f(\theta) = \cos \theta" /></p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><strong>Interpretation:</strong> Tracking the horizontal distance (x-value) of a point rotating around the unit circle</li>
                <li><strong>Shape:</strong> Starts at <InlineMath math="(0,1)" />, down through <InlineMath math="(\frac{\pi}{2}, 0)" /> to <InlineMath math="(\pi, -1)" />, up through <InlineMath math="(\frac{3\pi}{2}, 0)" />, back to <InlineMath math="(2\pi, 1)" /></li>
                <li><strong>Symmetry:</strong> Even function: <InlineMath math="\cos(-\theta) = \cos \theta" /></li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Sinusoidal Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <KeyTerm 
              term="Sinusoidal Function" 
              definition="Any function that can be written as a transformation of the sine function (this includes cosine, which is just a shifted sine)."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Characteristics</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Amplitude (A)</h4>
                <p className="text-sm text-slate-700 mt-1">Half the distance between the maximum and minimum values.</p>
                <BlockMath math="A = \frac{\text{Max} - \text{Min}}{2}" />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Midline (y = d)</h4>
                <p className="text-sm text-slate-700 mt-1">The horizontal axis of oscillation.</p>
                <BlockMath math="d = \frac{\text{Max} + \text{Min}}{2}" />
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Period (p)</h4>
                <p className="text-sm text-slate-700 mt-1">The horizontal length of one cycle.</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-800">Frequency</h4>
                <p className="text-sm text-slate-700 mt-1">Reciprocal of the period (<InlineMath math="1/p" />). The number of cycles per unit of input.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concavity</h3>
            <ImportantNote>
              Sinusoidal functions oscillate between <strong>concave up</strong> and <strong>concave down</strong>. Inflection points occur where the graph crosses the midline.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-6",
      title: "Topic 3.6: Sinusoidal Function Transformations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">General Forms</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="f(x) = a \sin(b(x - c)) + d" />
              <BlockMath math="g(x) = a \cos(b(x - c)) + d" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Analyzing Parameters</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">|a| (Amplitude)</h4>
                <p className="text-sm text-slate-700 mt-1">Vertical dilation factor. If <InlineMath math="a < 0" />, reflects over the midline.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">d (Vertical Shift)</h4>
                <p className="text-sm text-slate-700 mt-1">Moves the midline up or down.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">b (Frequency Parameter)</h4>
                <p className="text-sm text-slate-700 mt-1">Horizontal dilation. <strong>Period</strong> <InlineMath math="= \frac{2\pi}{|b|}" /></p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-800">c (Phase Shift)</h4>
                <p className="text-sm text-slate-700 mt-1">Horizontal translation.</p>
                <ImportantNote>
                  You must factor out <InlineMath math="b" /> to see the phase shift clearly. If given <InlineMath math="y = \sin(bx - h)" />, rewrite as <InlineMath math="y = \sin(b(x - \frac{h}{b}))" />. The shift is <InlineMath math="h/b" />.
                </ImportantNote>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Graphing Strategy</h3>
            <ol className="list-decimal pl-5 space-y-2 text-slate-700">
              <li>Identify Midline (<InlineMath math="d" />)</li>
              <li>Identify Max/Min lines (<InlineMath math="d \pm |a|" />)</li>
              <li>Determine Period (<InlineMath math="2\pi/b" />) and divide into 4 "quarter-points"</li>
              <li>Apply Phase Shift (<InlineMath math="c" />) to the starting x-value</li>
            </ol>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-7",
      title: "Topic 3.7: Sinusoidal Function Context and Data Modeling",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Modeling from Data Points</h3>
            <p className="text-slate-700 mb-4">Given data (e.g., tides, temperature, circular motion):</p>
            <ol className="list-decimal pl-5 space-y-2 text-slate-700">
              <li><strong>Find Midline (d):</strong> Average of highest and lowest output values</li>
              <li><strong>Find Amplitude (a):</strong> Distance from midline to max</li>
              <li><strong>Find Period:</strong> Identify change in input to go from Max to Max (one period) or Max to Min (half period). Solve <InlineMath math="b = 2\pi / \text{Period}" /></li>
              <li><strong>Find Phase Shift (c):</strong>
                <ul className="list-disc pl-5 mt-1 text-sm">
                  <li>If using <strong>cosine</strong>: Find the x-value of the Maximum. Use <InlineMath math="f(x) = a \cos(b(x - x_{max})) + d" /></li>
                  <li>If using <strong>negative cosine</strong>: Find the x-value of the Minimum</li>
                  <li>If using <strong>sine</strong>: Find the x-value where graph crosses midline going <em>up</em></li>
                </ul>
              </li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Technology</h3>
            <ImportantNote>
              Students must be able to use a graphing calculator to perform a <strong>Sinusoidal Regression</strong> (<InlineMath math="y = a \sin(bx+c)+d" />) to fit a model to raw data.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-8",
      title: "Topic 3.8: The Tangent Function",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Graph of Tangent</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <p className="font-semibold text-purple-800"><InlineMath math="f(\theta) = \tan \theta" /></p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><strong>Definition:</strong> Slope of the terminal ray</li>
                <li><strong>Period:</strong> <InlineMath math="\pi" /> (NOT <InlineMath math="2\pi" />)</li>
                <li><strong>Asymptotes:</strong> Where slope is undefined (vertical line): <InlineMath math="\theta = \frac{\pi}{2} + n\pi" /></li>
                <li><strong>Zeros:</strong> Where slope is 0 (horizontal line): <InlineMath math="\theta = n\pi" /></li>
                <li><strong>Behavior:</strong> Always increasing between asymptotes</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Transformations</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="f(x) = a \tan(b(x-c)) + d" />
              <p className="text-sm text-slate-700 mt-2"><strong>Period</strong> <InlineMath math="= \frac{\pi}{|b|}" /></p>
              <p className="text-sm text-slate-600 mt-1"><InlineMath math="a" /> affects steepness near the inflection point but does <em>not</em> define an amplitude (range is infinite).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-9",
      title: "Topic 3.9: Inverse Trigonometric Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concept of Inverse</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Since trig functions are periodic, they fail the Horizontal Line Test and are not one-to-one. To define inverse functions, we must <strong>restrict the domain</strong> of the original function.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Domain Restrictions (Memorize!)</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Arcsine (<InlineMath math="y = \sin^{-1} x" />)</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Input:</strong> <InlineMath math="[-1, 1]" /></p>
                <p className="text-sm text-slate-700"><strong>Output (Range):</strong> <InlineMath math="[-\frac{\pi}{2}, \frac{\pi}{2}]" /> (Quadrants I and IV)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Arccosine (<InlineMath math="y = \cos^{-1} x" />)</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Input:</strong> <InlineMath math="[-1, 1]" /></p>
                <p className="text-sm text-slate-700"><strong>Output (Range):</strong> <InlineMath math="[0, \pi]" /> (Quadrants I and II)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Arctangent (<InlineMath math="y = \tan^{-1} x" />)</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Input:</strong> <InlineMath math="(-\infty, \infty)" /></p>
                <p className="text-sm text-slate-700"><strong>Output (Range):</strong> <InlineMath math="(-\frac{\pi}{2}, \frac{\pi}{2})" /> (Quadrants I and IV)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpretation</h3>
            <ImportantNote>
              <InlineMath math="y = \sin^{-1}(x)" /> means "y is the angle (in the restricted range) whose sine is x."
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-10",
      title: "Topic 3.10: Trigonometric Equations and Inequalities",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Solving Equations</h3>
            <ol className="list-decimal pl-5 space-y-2 text-slate-700">
              <li><strong>Isolate the function:</strong> Get <InlineMath math="\sin x = \text{number}" /></li>
              <li><strong>Find Reference Angle:</strong> Use special triangles or inverse trig</li>
              <li><strong>Check Quadrants:</strong> Determine which quadrants satisfy the sign of the number</li>
              <li><strong>General Solutions:</strong> Add <InlineMath math="+ 2\pi n" /> (for sin/cos) or <InlineMath math="+ \pi n" /> (for tan)</li>
              <li><strong>Interval Solutions:</strong> If asked for <InlineMath math="x \in [0, 2\pi)" />, only list values in that range</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Inequalities</h3>
            <p className="text-slate-700 leading-relaxed">
              Find the critical points where the equality holds. Use a sign chart or a graph to determine the intervals where the inequality is true (e.g., where is the sine graph <em>above</em> 0.5?).
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-11",
      title: "Topic 3.11: The Secant, Cosecant, and Cotangent Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definitions (Reciprocals)</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Secant</h4>
                <BlockMath math="\sec \theta = \frac{1}{\cos \theta}" />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Cosecant</h4>
                <BlockMath math="\csc \theta = \frac{1}{\sin \theta}" />
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Cotangent</h4>
                <BlockMath math="\cot \theta = \frac{1}{\tan \theta} = \frac{\cos \theta}{\sin \theta}" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Graphs and Asymptotes</h3>
            <div className="space-y-3">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-700"><InlineMath math="\sec \theta" /> has VA where <InlineMath math="\cos \theta = 0" /> (<InlineMath math="\pi/2 + n\pi" />)</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-700"><InlineMath math="\csc \theta" /> has VA where <InlineMath math="\sin \theta = 0" /> (<InlineMath math="n\pi" />)</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-700"><InlineMath math="\cot \theta" /> has VA where <InlineMath math="\sin \theta = 0" /> (<InlineMath math="n\pi" />)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Ranges</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><InlineMath math="\sec" /> and <InlineMath math="\csc" />: <InlineMath math="(-\infty, -1] \cup [1, \infty)" /></p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-sm text-slate-700"><InlineMath math="\cot" />: <InlineMath math="(-\infty, \infty)" /></p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-12",
      title: "Topic 3.12: Equivalent Representations (Identities)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Pythagorean Identities</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <BlockMath math="\sin^2 \theta + \cos^2 \theta = 1" />
                <p className="text-sm text-slate-600 mt-1">(Most important - memorize this!)</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <BlockMath math="\tan^2 \theta + 1 = \sec^2 \theta" />
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <BlockMath math="1 + \cot^2 \theta = \csc^2 \theta" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sum and Difference Identities</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="\sin(\alpha \pm \beta) = \sin\alpha \cos\beta \pm \cos\alpha \sin\beta" />
              <BlockMath math="\cos(\alpha \pm \beta) = \cos\alpha \cos\beta \mp \sin\alpha \sin\beta" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Double Angle Identities</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <BlockMath math="\sin(2\theta) = 2\sin\theta \cos\theta" />
              <BlockMath math="\cos(2\theta) = \cos^2\theta - \sin^2\theta" />
              <p className="text-sm text-slate-600 mt-2">Also: <InlineMath math="2\cos^2\theta - 1" /> or <InlineMath math="1 - 2\sin^2\theta" /></p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-13",
      title: "Topic 3.13: Trigonometry and Polar Coordinates",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Polar System</h3>
            <p className="text-slate-700 mb-4">
              Instead of <InlineMath math="(x, y)" />, points are defined by <InlineMath math="(r, \theta)" />.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><InlineMath math="r" />: Directed distance from the pole (origin)</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-sm text-slate-700"><InlineMath math="\theta" />: Angle from the polar axis (positive x-axis)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Coordinate Conversion</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Polar to Rectangular</h4>
                <BlockMath math="x = r \cos \theta" />
                <BlockMath math="y = r \sin \theta" />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Rectangular to Polar</h4>
                <BlockMath math="r^2 = x^2 + y^2" />
                <BlockMath math="\tan \theta = \frac{y}{x}" />
                <p className="text-sm text-slate-600 mt-1">(Must analyze quadrants to find correct <InlineMath math="\theta" />)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Complex Numbers in Polar Form</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-slate-700">A complex number <InlineMath math="z = a + bi" /> can be written in polar form:</p>
              <BlockMath math="z = r(\cos \theta + i \sin \theta)" />
              <p className="text-sm text-slate-600 mt-2">where <InlineMath math="r = |z|" /> (modulus/magnitude)</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-14",
      title: "Topic 3.14: Polar Function Graphs",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Graphs are defined by <InlineMath math="r = f(\theta)" />. As <InlineMath math="\theta" /> rotates, the distance from the origin <InlineMath math="r" /> changes.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Common Polar Curves</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Circles</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="r = a" /> (centered at pole) or <InlineMath math="r = a \cos \theta" /> / <InlineMath math="r = a \sin \theta" /> (tangent to pole)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Limaçons</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="r = a \pm b \cos \theta" /> or <InlineMath math="r = a \pm b \sin \theta" /></p>
                <ul className="list-disc pl-5 text-sm text-slate-600 mt-1">
                  <li>If <InlineMath math="|a| = |b|" />, it's a <strong>Cardioid</strong> (heart shape)</li>
                  <li>If <InlineMath math="|a| < |b|" />, it has an inner loop</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Rose Curves</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="r = a \cos(n\theta)" /> or <InlineMath math="r = a \sin(n\theta)" /></p>
                <ul className="list-disc pl-5 text-sm text-slate-600 mt-1">
                  <li>If <InlineMath math="n" /> is odd: <InlineMath math="n" /> petals</li>
                  <li>If <InlineMath math="n" /> is even: <InlineMath math="2n" /> petals</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-15",
      title: "Topic 3.15: Rates of Change in Polar Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Rate of Change of r with respect to θ</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We analyze <InlineMath math="\frac{\Delta r}{\Delta \theta}" /> or the instantaneous rate of change of <InlineMath math="r" />.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Positive Rate</h4>
                <p className="text-sm text-slate-700 mt-1">As <InlineMath math="\theta" /> increases, the point moves <strong>away</strong> from the pole (radius gets larger).</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Negative Rate</h4>
                <p className="text-sm text-slate-700 mt-1">As <InlineMath math="\theta" /> increases, the point moves <strong>toward</strong> the pole (radius gets smaller).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Average Rate of Change</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="\text{AROC} = \frac{r(\theta_2) - r(\theta_1)}{\theta_2 - \theta_1}" />
              <p className="text-sm text-slate-600 mt-2">Measures the average change in radius per radian of rotation.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Extrema</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><strong>Relative Maxima of r:</strong> Points furthest from the pole</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-sm text-slate-700"><strong>Zeros of r:</strong> When the graph passes through the pole (origin)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "summary-3",
      title: "Unit 3 Summary: Key Skills",
      content: (
        <div className="space-y-6">
          <section>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Unit Circle Values</h4>
                  <p className="text-sm text-slate-700 mt-1">Memorize sine and cosine values for special angles (0, π/6, π/4, π/3, π/2).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Sinusoidal Transformations</h4>
                  <p className="text-sm text-slate-700 mt-1">Apply <InlineMath math="a \sin(b(x-c))+d" />: amplitude, period (<InlineMath math="2\pi/b" />), phase shift, midline.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="bg-purple-200 text-purple-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Inverse Trig Ranges</h4>
                  <p className="text-sm text-slate-700 mt-1">Know restricted domains: arcsin/arctan output in Q1/Q4, arccos output in Q1/Q2.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="bg-amber-200 text-amber-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Identities</h4>
                  <p className="text-sm text-slate-700 mt-1">Apply Pythagorean, sum/difference, and double angle identities.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="bg-red-200 text-red-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Polar Coordinates</h4>
                  <p className="text-sm text-slate-700 mt-1">Convert between rectangular and polar. Recognize circles, limaçons, and rose curves.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default precalculusUnit3Notes;
